import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { routes } from 'routes';
import * as yup from 'yup';

export enum BenchmarkScreen {
  editBenchmark = 'editBenchmark',
  createBenchmark = 'createBenchmark',
  editTimeWindows = 'editTimeWindows',
  addTimeWindow = 'addTimeWindow',
}

export const benchmarkScreen = 'screen';

type Props = {
  setValue: any;
  control?: any;
  timeWindowSchema: yup.AnySchema;
};

export const useTWScreenParams = (benchmarkId?: string) => {
  const { search } = useLocation();
  const urlSearch = new URLSearchParams(search);
  const page = urlSearch.get(benchmarkScreen) || BenchmarkScreen.editBenchmark;
  if (!benchmarkId) {
    return {
      isTimeWindowsScreen: false,
      addTimeWindow: true,
      benchmarkScreen: BenchmarkScreen.createBenchmark,
    };
  }

  return {
    isTimeWindowsScreen: [
      BenchmarkScreen.addTimeWindow,
      BenchmarkScreen.editTimeWindows,
    ].includes(page as BenchmarkScreen),
    addTimeWindow: page === BenchmarkScreen.addTimeWindow,
    benchmarkScreen: page as BenchmarkScreen,
  };
};

export const useTimeWindows = <T>({
  setValue,
  control,
  timeWindowSchema,
}: Props) => {
  const [timeWindowsFilled, setTimeWindowsFilled] = useState(false);

  const timeWindows =
    useWatch({
      control,
      name: 'timeWindows',
      defaultValue: [],
    }) || [];

  const timeWindowsValidationSchema = useMemo(() => {
    return yup.array().of(timeWindowSchema);
  }, [timeWindowSchema]);

  useEffect(() => {
    async function checkIfValid() {
      if (timeWindows && timeWindowsValidationSchema) {
        const isValid = await timeWindowsValidationSchema.isValid(timeWindows);
        setTimeWindowsFilled(isValid);
      }
    }

    checkIfValid();
  }, [timeWindows, timeWindowsValidationSchema]);

  return { timeWindows, timeWindowsFilled };
};

export const useTWNavigation = (benchmarkScreen?: BenchmarkScreen) => {
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);

  const handleNavigation = () => {
    switch (benchmarkScreen) {
      case BenchmarkScreen.addTimeWindow:
      case BenchmarkScreen.editTimeWindows:
        searchParams.delete('screen');
        history.replace({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
        return;

      case BenchmarkScreen.createBenchmark:
        history.push({
          pathname: routes.getBenchmarksRoute(),
          search: searchParams.toString(),
        });
        return;
    }
  };

  return handleNavigation;
};
